import { useEffect, useState } from "react";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ReadingExtrasParent from "@/components/reading/ReadingExtrasParent";
import LibraryWeek from "@/components/reading/LibraryWeek";

const T = {
  en: { title: "Reading with Kiko", style: "Reading style", petit: "Little reader kit", grand: "Big reader kit", level: "Current level",
    current: "Working on", mastered: "Mastered lessons", review: "Sounds to review", week: "This week", none: "None yet",
    locked: "Not subscribed", ex: (n, m) => `${n} exercises · about ${m} min`, langs: { en: "English Reading", fr: "Lecture en français" } },
  fr: { title: "Lecture avec Kiko", style: "Style de lecture", petit: "Kit Petit lecteur", grand: "Kit Grand lecteur", level: "Niveau actuel",
    current: "En cours", mastered: "Leçons maîtrisées", review: "Sons à revoir", week: "Cette semaine", none: "Aucune pour l'instant",
    locked: "Pas d'abonnement", ex: (n, m) => `${n} exercices · environ ${m} min`, langs: { en: "English Reading", fr: "Lecture en français" } },
};

// Parent view of the Reading module + choice of the Petit / Grand lecteur style.
export default function ReadingReport({ childId, lang = "en" }) {
  const [rep, setRep] = useState(null);
  const [style, setStyle] = useState(null);
  const t = T[lang] || T.en;
  useEffect(() => {
    api.get(`/reading/${childId}/report`).then(({ data }) => setRep(data)).catch(() => setRep(false));
    api.get(`/reading/${childId}/home`).then(({ data }) => setStyle(data.style)).catch(() => {});
  }, [childId]);
  const pick = async (s) => {
    await api.post(`/reading/${childId}/style`, { style: s });
    setStyle(s);
    toast.success(lang === "fr" ? "Style enregistré" : "Style saved");
  };
  if (!rep) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 mt-6" data-testid="reading-report">
      <h2 className="font-bold text-lg flex items-center gap-2"><BookOpen size={18} className="text-green-600" /> {t.title}</h2>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-500">{t.style} :</span>
        {["petit", "grand"].map((s) => (
          <button key={s} onClick={() => pick(s)} data-testid={`reading-style-${s}`}
            className={`rounded-full border px-3 h-8 text-xs font-bold transition-colors ${style === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:bg-slate-50"}`}>
            {t[s]}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        {["en", "fr"].map((l) => {
          const r = rep[l];
          return (
            <div key={l} className="rounded-xl bg-slate-50 p-4 text-sm space-y-1.5" data-testid={`reading-report-${l}`}>
              <p className="font-bold">{t.langs[l]}</p>
              {!r.access && !r.mastered.length ? <p className="text-slate-400">{t.locked}</p> : (
                <>
                  <p><span className="text-slate-500">{t.level} :</span> {r.level} {r.current_title ? `· ${t.current} : ${r.current_title}` : ""}</p>
                  <p><span className="text-slate-500">{t.mastered} :</span> {r.mastered.length ? r.mastered.map((m) => m.id).join(", ") : t.none}</p>
                  <p><span className="text-slate-500">{t.review} :</span> {r.to_review.length ? r.to_review.map((s) => s.replace(/^(sound|son)_/, "")).join(", ") : t.none}</p>
                  <p><span className="text-slate-500">{t.week} :</span> {t.ex(r.week_exercises, r.week_minutes)}</p>
                </>
              )}
            </div>
          );
        })}
      </div>
      <LibraryWeek rep={rep} lang={lang} />
      <ReadingExtrasParent childId={childId} lang={lang} />
    </div>
  );
}
